import React, { useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { AccountApi, V2RegistrationData } from '../../services/AccountApi'
import { LoginApi } from '../../components/login/LoginApi'
import { toastError } from '../../shared/toastr'
import { Link, useNavigate } from 'react-router-dom'
import { Spinner } from '../../services/Spinner'
import { useOfferionTranslation } from '../../shared/store/hooks/useOfferionTranslation'
import { enableUserbackReplay, setUserbackUser } from '../../shared/utils/userback'
import { trackUserViaGtm } from '../../shared/utils/googleTagManager'
import rapidSSL from '../assets/icons/rapidSSL.svg'
import { FormInput } from '../shared/form/FormInput'
import { Button } from '../shared/Button'
import { TestimonialCarousel } from './TestimonialCarousel'
import { BackToLoginLink } from '../components/ui/back-to-login-link'
import { FormField } from '../shared/form/FormField'
import { useUserStore } from '../../shared/store/userStore'
import { useOnboardingStore } from '../../shared/store/onboardingStore'
// import { useCompanyStore } from '../../shared/store/companyStore'

const questions = [
    {
        questionKey: 'Registration.securityQuestion1',
        answerKeys: ['Registration.securityQuestion1Answer1', 'Registration.securityQuestion1Answer2'],
    },
    {
        questionKey: 'Registration.securityQuestion2',
        answerKeys: ['Registration.securityQuestion2Answer1', 'Registration.securityQuestion2Answer2'],
    },
    {
        questionKey: 'Registration.securityQuestion3',
        answerKeys: ['Registration.securityQuestion3Answer1', 'Registration.securityQuestion3Answer2'],
    },
    {
        questionKey: 'Registration.securityQuestion4',
        answerKeys: ['Registration.securityQuestion4Answer1'],
    },
    {
        questionKey: 'Registration.securityQuestion5',
        answerKeys: ['Registration.securityQuestion5Answer1', 'Registration.securityQuestion5Answer2'],
    },
]

type V2RegistrationForm = Omit<V2RegistrationData, 'isAccountingVat' | 'firstName' | 'lastName' | 'companyType'> & {
    doesHaveVat: 'vatEnabled' | 'vatDisabled'
    fullName: string
    isLawyer: boolean
    isWindowMaker: boolean
}

export const RegistrationContainer = () => {
    const navigate = useNavigate()
    const { t } = useOfferionTranslation()
    const [selectedQuestion] = useState(questions[Math.floor(Math.random() * 5)])
    const updateAuthenticatedUser = useUserStore(state => state.updateAuthenticatedUser)
    const setShowOnboardingOnStart = useOnboardingStore(state => state.setShowOnboardingOnStart)
    const setPendingAutofill = useOnboardingStore(state => state.setPendingAutofill)

    const schema = Yup.object().shape({
        fullName: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('Registration.personaldataName') })),
        email: Yup.string()
            .email(t('Allaround.Validation.mail', { fieldName: t('Registration.personaldataEmail') }))
            .required(t('Allaround.Validation.required', { fieldName: t('Registration.personaldataEmail') })),
        companyName: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('Registration.accountdataName') })),
        isLawyer: Yup.boolean(),
        isWindowMaker: Yup.boolean(),
        oib: Yup.string().required(t('Allaround.Validation.required', { fieldName: t('Registration.accountdataVATNumber') })),
        password: Yup.string()
            .required(t('Allaround.Validation.required', { fieldName: t('Registration.accountdataPassword') }))
            .min(6, t('Allaround.Validation.minLength', { fieldName: t('Registration.accountdataPassword'), minLength: 6 })),
        questionAnswer: Yup.string()
            .required(t('Allaround.Validation.required', { fieldName: t('Registration.securityQuestionTitle') }))
            .test('Check answer', t('Allaround.Validation.invalidAnswer'), function (val) {
                if (val === null || val === undefined) return false

                return selectedQuestion.answerKeys.map(x => t(x).toLocaleLowerCase()).includes(val.trim().toLocaleLowerCase())
            }),

        privacyPolicy: Yup.boolean().oneOf(
            [true],
            t('Allaround.Validation.mustAccept', { fieldName: t('Registration.PrivacyPolicyLabel') })
        ),
        termsAndConditions: Yup.boolean().oneOf(
            [true],
            t('Allaround.Validation.mustAccept', { fieldName: t('Registration.TermsAndConditionsLabel') })
        ),
    })

    const onSubmit = async (formData: V2RegistrationForm) => {
        const [firstName, lastName] = splitName(formData.fullName)
        let companyType
        if (formData.isLawyer) {
            companyType = '1'
        } else if (formData.isWindowMaker) {
            companyType = '2'
        } else {
            companyType = '0'
        }

        const data: V2RegistrationData = {
            ...formData,
            isAccountingVat: formData.doesHaveVat === 'vatEnabled',
            firstName,
            lastName,
            companyType: companyType,
        }

        try {
            Spinner.show()

            await AccountApi.register(data)
        } catch (e: any) {
            const isValidationError = e && e.response && e.response.data && e.response.data.modelState && e.response.data.modelState

            if (isValidationError) {
                Object.values<string[]>(e.response.data.modelState).forEach((r: string[]) => toastError(r[0]))
            } else {
                toastError('Allaround.Message.unknownError')
            }

            return
        } finally {
            Spinner.hide()
        }

        const result = await LoginApi.login(data.email, data.password)

        trackUserViaGtm(result)
        setUserbackUser(result)
        enableUserbackReplay()

        updateAuthenticatedUser({ ...result, authenticated: true })
        setShowOnboardingOnStart(true)

        navigate('/dashboard')
    }

    return (
        <div className="v2-root">
            <main className="w-full h-full flex">
                <div className="h-full w-full lg:w-1/2">
                    <BackToLoginLink />

                    <div className="w-full h-full mx-auto xl:ml-auto xl:mr-[15%] max-w-[478px] sm:min-w-[478px] mb-10 px-5 pt-6 md:px-8 pb-7">
                        <div className="flex flex-col items-start md:items-center lg:items-start mb-8 md:mb-[54px]">
                            <h1 className="text-xl text-grey100">{t('Registration.title')}</h1>
                            <p className="text-base md:text-balance text-grey90">{t('Registration.promoText')}</p>
                        </div>

                        <Formik
                            validationSchema={schema}
                            initialValues={{
                                fullName: '',
                                email: '',
                                password: '',
                                companyName: '',
                                isLawyer: false,
                                isWindowMaker: false,
                                companyVat: 'false',
                                oib: '',
                                questionAnswer: '',
                                doesHaveVat: 'vatDisabled',
                                privacyPolicy: false,
                                termsAndConditions: false,
                            }}
                            onSubmit={onSubmit}
                        >
                            {({ setFieldValue, isSubmitting }) => (
                                <Form noValidate={true} className="flex flex-col gap-[22px]">
                                    <OibAutofillWatcher
                                        onAutofill={data => {
                                            if (!data) {
                                                setPendingAutofill(null)
                                                return
                                            }

                                            const street = data.headquatersStreet ?? data.headquatersAddress ?? undefined
                                            const streetNumber = data.headquatersStreetNumber ?? undefined
                                            const country = data.countryText?.toUpperCase() ?? undefined

                                            setPendingAutofill({
                                                name: data.name ?? data.shortName ?? '',
                                                street,
                                                streetNumber,
                                                city: data.headquatersCity ?? '',
                                                postalCode: data.headquatersZip ?? '',
                                                country,
                                                contactEmail: data.emailAddress ?? undefined,
                                            })
                                        }}
                                    />
                                    <fieldset>
                                        <legend className="text-sm mb-2.5">{t('Registration.personaldataTitle')}</legend>
                                        <div className="bg-grey10 flex flex-col gap-[14px] border border-solid border-border rounded-lg px-4 py-[18px]">
                                            <FormField
                                                component={FormInput}
                                                name="fullName"
                                                label={t('V2.Registration.personaldataNameLastName')}
                                                placeholder={t('V2.Registration.personaldataNameLastNamePlaceholder')}
                                            />
                                            <FormField
                                                component={FormInput}
                                                name="email"
                                                type="email"
                                                label={t('Registration.personaldataEmail')}
                                                placeholder={t('Registration.personaldataEmailPlaceholder')}
                                            />
                                            <FormField
                                                component={FormInput}
                                                name="password"
                                                type="password"
                                                label={t('Registration.accountdataPassword')}
                                                placeholder={t('Registration.accountdataPasswordPlaceholder')}
                                            />
                                        </div>
                                    </fieldset>

                                    <fieldset className="min-w-0 max-w-full">
                                        <legend className="text-sm mb-2.5">{t('Registration.accountdataTitle')}</legend>
                                        <div className="bg-grey10 flex flex-col gap-[14px] border border-solid border-border rounded-lg px-4 py-[18px]">
                                            <FormField
                                                component={FormInput}
                                                name="companyName"
                                                label={t('Registration.accountdataName')}
                                                placeholder={t('Registration.accountdataNamePlaceholder')}
                                            />

                                            <div>
                                                <label className="text-grey100 font-sm-bold">
                                                    {t('V2.Registration.subjectTypeCheckLabel')}
                                                </label>

                                                <div className="flex gap-2.5 items-center">
                                                    <Field
                                                        id="isLawyer"
                                                        name="isLawyer"
                                                        type="checkbox"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFieldValue('isLawyer', e.target.checked)
                                                            if (e.target.checked) {
                                                                setFieldValue('isWindowMaker', false)
                                                            }
                                                        }}
                                                    />
                                                    <label className="cursor-pointer" htmlFor="isLawyer">
                                                        {t('V2.Registration.subjectTypeLawyer')}
                                                    </label>
                                                </div>
                                                <div className="flex gap-2.5 items-center">
                                                    <Field
                                                        id="isWindowMaker"
                                                        name="isWindowMaker"
                                                        type="checkbox"
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setFieldValue('isWindowMaker', e.target.checked)
                                                            if (e.target.checked) {
                                                                setFieldValue('isLawyer', false)
                                                            }
                                                        }}
                                                    />
                                                    <label className="cursor-pointer" htmlFor="isWindowMaker">
                                                        {t('V2.Registration.subjectTypewindowMaker')}
                                                    </label>
                                                </div>
                                            </div>

                                            <FormField
                                                component={FormInput}
                                                name="oib"
                                                label={t('Registration.accountdataVATNumber')}
                                                inputMode="numeric"
                                                placeholder={t('Registration.accountdataVATNumberPlaceholder')}
                                            />
                                            <div className="flex flex-col md:flex-row md:justify-between gap-2.5">
                                                <label htmlFor="companyVat">{t('Registration.accountdataVATSelector')}</label>
                                                <div className="flex gap-5">
                                                    <label className="flex gap-1.5 items-center cursor-pointer">
                                                        <Field id="companyVat" type="radio" name="doesHaveVat" value="vatEnabled" />
                                                        <span className="text-sm">{t('Allaround.Button.yes')}</span>
                                                    </label>
                                                    <label className="flex gap-1.5 items-center cursor-pointer">
                                                        <Field id="companyVat" type="radio" name="doesHaveVat" value="vatDisabled" />
                                                        <span className="text-sm">{t('Allaround.Button.no')}</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>

                                    <fieldset>
                                        <legend className="text-sm mb-2.5">{t('Registration.securityQuestionTitle')}</legend>
                                        <div className="bg-grey10 flex flex-col gap-[14px] border border-solid border-border rounded-lg px-4 py-[18px]">
                                            <FormField
                                                component={FormInput}
                                                name="questionAnswer"
                                                label={t(selectedQuestion.questionKey)}
                                                placeholder={t('Registration.securityQuestionPlaceholder')}
                                            />

                                            <div>
                                                <img src={rapidSSL} alt="RapidSSL Security" />
                                            </div>
                                        </div>
                                    </fieldset>

                                    <div>
                                        <span className="text-base text-grey90 mr-1.5">{t('Registration.alreadyHaveAccount')}</span>

                                        <Link to="/login" className="text-base-bold text-blue40">
                                            {t('Registration.login')}
                                        </Link>
                                    </div>

                                    <div className="flex flex-col gap-2.5">
                                        <div className="flex gap-2.5 items-center">
                                            <Field id="privacyPolicy" name="privacyPolicy" type="checkbox" />
                                            <label className="cursor-pointer" htmlFor="privacyPolicy">
                                                {t('Registration.IAcceptPrefix')} {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                                <a
                                                    href={t('Registration.PrivacyPolicyLink')}
                                                    target="_blank"
                                                    rel="noopener"
                                                    className="text-sm-bold text-blue40 underline"
                                                >
                                                    {t('Registration.PrivacyPolicyLabel')}
                                                </a>
                                            </label>
                                        </div>
                                        <ErrorMessage name={'privacyPolicy'} component="span" className="text-sm text-red20" />

                                        <div className="flex gap-2.5 items-center">
                                            <Field id="termsAndConditions" name="termsAndConditions" type="checkbox" />
                                            <label className="cursor-pointer" htmlFor="termsAndConditions">
                                                {t('Registration.IAcceptPrefix')} {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                                <a
                                                    href={t('Registration.TermsAndConditionsLink')}
                                                    target="_blank"
                                                    rel="noopener"
                                                    className="text-sm-bold text-blue40 underline"
                                                >
                                                    {t('Registration.TermsAndConditionsLabel')}
                                                </a>
                                            </label>
                                        </div>
                                        <ErrorMessage name={'termsAndConditions'} component="span" className="text-sm text-red20" />
                                    </div>

                                    <Button disabled={isSubmitting} loading={isSubmitting}>
                                        {t('Registration.registerButton')}
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>

                <TestimonialCarousel />
            </main>
        </div>
    )
}

function splitName(fullName: string) {
    fullName = fullName.trim()
    const firstSpace = fullName.indexOf(' ')

    if (firstSpace !== -1) {
        const firstName = fullName.slice(0, firstSpace).trim()
        const lastName = fullName.slice(firstSpace + 1).trim()

        return [firstName, lastName]
    } else {
        // no space found, use the whole name as first name and the last name,
        // otherwise validation wouldn't pass
        return [fullName, fullName]
    }
}

function OibAutofillWatcher({ onAutofill }: { onAutofill: (data: any | null) => void }) {
    const { values } = useFormikContext<any>()
    const oib: string = values?.oib || ''
    const lastRequestedRef = React.useRef<string>('')

    useEffect(() => {
        const taxNumber = (oib || '').trim()
        if (!taxNumber) {
            if (lastRequestedRef.current !== '') lastRequestedRef.current = ''
            onAutofill(null)
            return
        }

        const startsWithLetter = /^[a-zA-Z]/.test(taxNumber)
        const requiredLength = startsWithLetter ? 13 : 11

        if (taxNumber.length !== requiredLength) {
            if (lastRequestedRef.current !== '') lastRequestedRef.current = ''
            onAutofill(null)
            return
        }

        if (lastRequestedRef.current === taxNumber) return
        lastRequestedRef.current = taxNumber
        ;(async () => {
            const mod = await import('../../services/ClientApi')
            const data: any = await mod.ClientApi.getAutofill(taxNumber)
            if (!data || !data.dataFound) {
                onAutofill(null)
                return
            }
            onAutofill(data)
        })()

        return
    }, [oib, onAutofill])

    return null
}
